/**
 * Frontend FD100 KV Generator
 * Creates 1080x1350 key visual images using HTML5 Canvas
 */

// Special positioning cases for project images
// alignX: 0% = left, 50% = center (default), 100% = right
const imagePositionOverrides = [
    { projectId: '67aa1011740d844a3d633e93', alignX: 85 },
    { projectId: '67e42d7043d5609350e32539', alignX: 35 },
    // Add more special cases here as needed
    // Example: { projectId: 'some_id', alignX: 25 } for slightly left of center
];

// Per-partenaire logo size/position overrides (keyed by lowercase name)
// scale: multiplier on auto-calculated size (default 1.0, <1 = smaller, >1 = bigger)
// offsetY: vertical pixel offset (negative = raise, positive = lower)
const partenaireLogoOverrides = {
    'ecole camondo': { offsetY: -15 },
    'carpenter workshop gallery': { scale: 2.5 },
    'oscar ono': { scale: 0.5 },
    'manufacture de tapis de bourgogne': { scale: 1.8 },
    'thg': { scale: 0.55 },
    'versant': { scale: 0.6 },
    'cinna': { scale: 0.75 },
    'trone': { scale: 0.6 },
    'casino 2000': { scale: 1.6 },
    'issé': { scale: 0.55 },
    'isse': { scale: 0.55 },
    'ceylon': { scale: 2.0 },
    'ceylan': { scale: 2.0 },
    'benetiere': { scale: 2.0 },
    'bénétière': { scale: 2.0 },
};

function getPartenaireOverride(name) {
    if (!name) return null;
    const key = name.toLowerCase().trim();
    return partenaireLogoOverrides[key] || null;
}

const wordingMapping = [
    { from: 'United States', to: 'USA' },
    { from: 'United Arab Emirates', to: 'UAE' },
    { from: 'Atelier De Création Geste.S', to: 'Atelier de création Geste.s' },
    { from: 'Studio Mhna', to: 'Studio MHNA' },
    { from: 'Pomo Museum', to: 'PoMo Museum' },
    { from: 'Endless Summer ii', to: 'Endless Summer II' },
    { from: 'el\'le architects', to: 'EL\'LE Architects' },
    { from: 'aw2 Architecture & interiors', to: 'AW2 Architecture & Interiors' },
    { from: 'Maxime d\'Angeac Architecture', to: 'Maxime d\'Angeac Architecture' },
    { from: 'Bureau Des Mésarchitectures // Didier Fiúza Faustino', to: 'Bureau Des Mésarchitectures //\nDidier Fiúza Faustino' },
    { from: 'Neophore: Transporting Light', to: 'Neophore: Transporting\nLight' },
    { from: '100 Colors Of Skirts Emmanuelle Moureaux X Cfcl', to: '100 Colors Of Skirts Emmanuelle\nMoureaux X Cfcl' },
    { from: 'Studio Delphine Maumot Interior Design & Collectible Design', to: 'Studio Delphine Maumot Interior\nDesign & Collectible Design' },
    { from: 'pierre yves rochon / anne cecile thirifays', to: 'Pierre Yves Rochon /\nAnne Cecile Thirifays' },
    { from: 'LA PERGOLA, ROME CAVALIERI, WALDORF ASTORIA', to: 'La Pergola, Rome Cavalieri,\nWaldorf Astoria' },
    { from: 'FRANCE PAVILION – EXPO 2025 OSAKA', to: 'France Pavilion – Expo\n2025 Osaka' },
    { from: 'La Casa Imperfetta – Triennale Milano Museum', to: 'La Casa Imperfetta – Triennale\nMilano Museum' },
    { from: 'KING’S ISLAND ECO-RESORT', to: 'King’s Island Eco-resort' },
    { from: 'Making NUNO Textiles, Art Tower Mito', to: 'Making NUNO Textiles,\nArt Tower Mito' },
    { from: 'JEAN-CHARLES DE CASTELBAJAC', to: 'Jean-Charles de Castelbajac' },
    { from: 'MONTBLANC Watches & Wonders exhibition', to: 'Montblanc Watches &\nWonders Exhibition'},
    { from: 'APPRT2 Caroline Grondin & Guillaume Avarguez', to: 'APPRT2\nCaroline Grondin &\nGuillaume Avarguez' },
    { from: 'ISHIGAKI TABLE LAMP - Moss Rose', to: 'Ishigaki Lamp - Moss Rose' },
    { from: 'FRANCE PAVILLION – EXPO 2025 OSAKA', to: 'France Pavilion –\nExpo 2025 Osaka' },
];

function applyWordingMapping(text) {
    let newText = text;
    wordingMapping.forEach(mapping => {
        if (newText.toLowerCase() === mapping.from.toLowerCase()) {
            newText = mapping.to;
        }
    });
    return newText;
}

// Helper function to get designer display name (same logic as backend)
function getEventDesignerDisplayName(project, user) {
    // First priority: admin override name
    if (project.override_designer_name) {
        return project.override_designer_name;
    }

    // Second priority: check if candidating as person for FD100
    if (project.candidate_as_person_fd100_25 === "oui") {
        const firstName = user.hsProfile?.firstname || '';
        const lastName = user.hsProfile?.lastname || '';
        return `${firstName} ${lastName}`.trim();
    }

    // Default: company name in uppercase
    return user.hsCompany?.properties?.name || '';
}

// Helper function to load an image and return a promise
function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Handle CORS
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

// Helper function to load a font
async function loadFont(fontName, fontUrl) {
    const font = new FontFace(fontName, `url(${fontUrl})`);
    await font.load();
    document.fonts.add(font);
}

/**
 * Generate FD100 KV image on the frontend
 * @param {Object} projectData - Full project data including user info
 * @param {boolean} inverted - Whether to use inverted colors (blue bg)
 * @param {string} image_mode - Image fit mode: 'contain', 'cover', or 'debug'
 * @param {Object} editedData - Optional edited data from Supabase { projectName, creatorName }
 * @param {string} partenaireLogoUrl - Optional partenaire logo URL to display at bottom right
 * @returns {Promise<Blob>} - Returns a PNG blob of the generated image
 */
export async function generateFD100KV(projectData, inverted = false, image_mode = 'cover', editedData = null, partenaireLogoUrl = null, partenaireName = null, skipWordingMapping = false) {
    const { project, user } = projectData;

    // Constants for layout
    const width = 1080;
    const height = 1350;
    const bgColor = inverted ? 'rgb(24, 51, 189)' : 'rgb(231, 231, 231)';
    const textColor = inverted ? 'rgb(231, 231, 231)' : 'rgb(24, 51, 189)';
    const topMargin = 42;
    const textLeftPadding = 30;
    const logoTopPadding = 15;
    const logoBottomMargin = 28;
    const logoSize = 260;
    const logoSpacing = 26;

    // Text sizes
    const projectNameSize = 60;
    const designerNameSize = 60;
    const projectNameHeight = Math.ceil(projectNameSize * 1.1);
    const designerNameHeight = Math.ceil(designerNameSize * 1.1);

    // Get the first image from the project to determine aspect ratio
    let sourceImagePath = null;
    let imgHorizontalPadding = 30; // Default value

    if (project.medias && project.medias.length > 0) {
        const firstImageMedia = project.medias.find(media => media.type.includes('image'));
        if (firstImageMedia) {
            sourceImagePath = firstImageMedia.path;

            // Load the image to check its aspect ratio
            const sourceImageUrl = process.env.REACT_APP_MEDIA_BASE_URL + 'userfiles/' + sourceImagePath;
            const tempImg = await loadImage(sourceImageUrl);
            const imageRatio = tempImg.width / tempImg.height;

            // If ratio < 0.9 (portrait/narrow), use larger padding
            if (imageRatio < 0.9) {
                imgHorizontalPadding = 205;
            }
        }
    }

    // Load fonts
    await loadFont('SuisseIntl-Bold', '/assets/SuisseIntl-SemiBold.ttf');
    await loadFont('SuisseIntl-LightItalic', '/assets/SuisseIntl-LightItalic.ttf');

    function capitalizeEachWord(val) {
        return val.toLowerCase().replace(/(^|[^\p{L}])(\p{L})/gu, (match, nonLetter, letter) => {
            return nonLetter + letter.toUpperCase();
        });
    }

    // Helper function to draw multi-line text (handles \n)
    function drawMultilineText(ctx, text, x, y, lineHeight) {
        const lines = text.split('\n');
        lines.forEach((line, index) => {
            ctx.fillText(line, x, y + (index * lineHeight));
        });
        return lines.length; // Return number of lines drawn
    }

    // Get project name and designer name (use edited data if provided)
    const rawProjectName = editedData?.projectName || project.name_en?.trim() || project.name?.trim() || '';
    const rawDesignerName = editedData?.creatorName || getEventDesignerDisplayName(project, user);
    const capitalize = (t) => capitalizeEachWord(t.trim());
    const maybeMap = (t) => skipWordingMapping ? t : applyWordingMapping(t);
    const projectName = maybeMap(capitalize(rawProjectName));
    const designerName = maybeMap(capitalize(rawDesignerName));

    // Get country from project address (skip if none found)
    let countryFinal = '';
    if (project.adresse) {
        const addressParts = project.adresse.split(',');
        const country = addressParts.length > 0 ? addressParts[addressParts.length - 1].trim() : '';
        const countryClean = country.replace(/[0-9]/g, '').trim();

        const countryTranslations = {
            'japon': 'Japan',
            'espagne': 'Spain'
        };
        countryFinal = countryTranslations[countryClean.toLowerCase()] || countryClean;
    }
    countryFinal = maybeMap(countryFinal);

    // Count lines in project name and designer name for proper layout calculation
    const projectNameLineCount = projectName.split('\n').length;
    const designerNameLineCount = designerName.split('\n').length;

    // Calculate image square position based on actual line counts
    const textSectionHeight = topMargin + (projectNameLineCount * projectNameHeight) + (designerNameLineCount * designerNameHeight) + 32;
    const bottomSectionHeight = logoBottomMargin + logoSize;
    const availableImageWidth = width - 2 * imgHorizontalPadding; // imgHorizontalPadding on each side
    const availableImageHeight = height - textSectionHeight - bottomSectionHeight - logoTopPadding;
    const imageTop = Math.floor(textSectionHeight);
    const imageLeft = Math.floor((width - availableImageWidth) / 2);

    if (!sourceImagePath) {
        throw new Error('No image found for this project');
    }

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    // Enable high-quality image smoothing for better logo/image rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Fill background
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, width, height);

    // Load other images in parallel (sourceImagePath was already loaded earlier for ratio check)
    const fd100LogoUrl = inverted ? '/assets/FD100_award_winner_blanc_rouge.png' : '/assets/FD100_award_winner_bleu.png';
    const ministereLogoUrl = '/assets/logo_ministere_culture.png';

    // Use offset overlay if any text contains line breaks
    const hasLineBreaks = projectName.includes('\n') || designerName.includes('\n');
    const overlayUrl = hasLineBreaks ? '/assets/overlay_lines_offset.png' : '/assets/overlay_lines.png';

    const [fd100LogoImg, ministereLogoImg, overlayImg] = await Promise.all([
        loadImage(fd100LogoUrl),
        loadImage(ministereLogoUrl),
        loadImage(overlayUrl)
    ]);

    // Use the already loaded image
    const sourceImageUrl = process.env.REACT_APP_MEDIA_BASE_URL + 'userfiles/' + sourceImagePath + "?fullsize=true";
    const projectImg = await loadImage(sourceImageUrl);

    // Draw project name (left-aligned with left padding)
    ctx.fillStyle = textColor;
    ctx.font = `${projectNameSize}px SuisseIntl-Bold`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.letterSpacing = "-1px"

    // Safari requires vertical offset adjustment
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const safariOffset = isSafari ? 13 : 0;

    // Split project name into lines and draw each line
    const projectNameLines = projectName.split('\n');
    const showCountry = countryFinal && countryFinal.length > 0;
    projectNameLines.forEach((line, index) => {
        const isLastLine = index === projectNameLines.length - 1;
        ctx.fillText(line + (isLastLine && showCountry ? ',' : ''), textLeftPadding, topMargin + (index * projectNameHeight) - safariOffset);
    });

    // Draw country in light italic after the project name (if available)
    if (showCountry) {
        const lastLine = projectNameLines[projectNameLines.length - 1];
        const lastLineWidth = ctx.measureText(lastLine + ',').width;

        ctx.save();
        ctx.font = `${projectNameSize}px SuisseIntl-LightItalic`;
        ctx.letterSpacing = "-1px"

        const xPosition = textLeftPadding + lastLineWidth;
        const yPosition = topMargin + ((projectNameLines.length - 1) * projectNameHeight) - safariOffset;
        ctx.transform(1, 0, 0, 1, xPosition, yPosition);
        ctx.fillText(' ' + countryFinal, 0, 0);

        ctx.restore();
    }

    // Draw designer name (left-aligned) - adjust position based on number of project name lines
    ctx.font = `${designerNameSize}px SuisseIntl-Bold`;
    const designerNameY = topMargin + (projectNameLines.length * projectNameHeight) - safariOffset;
    drawMultilineText(ctx, designerName, textLeftPadding, designerNameY, designerNameHeight);

    // Draw project image - resize to fit within available space while maintaining aspect ratio
    const projectImgAspect = projectImg.width / projectImg.height;
    const availableAspect = availableImageWidth / availableImageHeight;

    let drawWidth, drawHeight, drawX, drawY;

    if (image_mode === 'cover') {
        // Cover mode: fill entire area, crop if necessary
        // Check for positioning override
        const positionOverride = imagePositionOverrides.find(override => override.projectId === project._id);
        const alignX = positionOverride ? positionOverride.alignX : 50; // Default 50% = center

        if (projectImgAspect > availableAspect) {
            // Image is wider - fit to height and crop sides
            drawHeight = availableImageHeight;
            drawWidth = availableImageHeight * projectImgAspect;
            // Apply horizontal alignment: 0% = left, 50% = center, 100% = right
            const offset = (availableImageWidth - drawWidth) * (alignX / 100);
            drawX = imageLeft + offset;
            drawY = imageTop;
        } else {
            // Image is taller - fit to width and crop top/bottom
            drawWidth = availableImageWidth;
            drawHeight = availableImageWidth / projectImgAspect;
            drawX = imageLeft;
            drawY = imageTop + (availableImageHeight - drawHeight) / 2;
        }
    } else {
        // Contain mode (default): fit entire image within available space
        if (projectImgAspect > availableAspect) {
            // Image is wider than available space
            drawWidth = availableImageWidth;
            drawHeight = availableImageWidth / projectImgAspect;
            drawX = imageLeft;
            drawY = imageTop + (availableImageHeight - drawHeight) / 2;
        } else {
            // Image is taller than available space
            drawHeight = availableImageHeight;
            drawWidth = availableImageHeight * projectImgAspect;
            drawX = imageLeft + (availableImageWidth - drawWidth) / 2;
            drawY = imageTop;
        }
    }

    // In cover mode, clip the image to the available area to prevent overflow
    if (image_mode === 'cover') {
        ctx.save();
        ctx.beginPath();
        ctx.rect(imageLeft, imageTop, availableImageWidth, availableImageHeight);
        ctx.clip();
        ctx.drawImage(projectImg, drawX, drawY, drawWidth, drawHeight);
        ctx.restore();
    } else {
        ctx.drawImage(projectImg, drawX, drawY, drawWidth, drawHeight);
    }

    // Debug mode: overlay red rectangle over image area
    if (image_mode === 'debug') {
        ctx.fillStyle = 'rgba(255, 0, 0, 0.2)';
        ctx.fillRect(imageLeft, imageTop, availableImageWidth, availableImageHeight);
    }

    // Calculate logo positions and sizes
    const logoY = Math.floor(height - logoBottomMargin - logoSize);

    // FD100 logo (bottom left) - centered with equal top/bottom margins, bottom-aligned with partenaire
    const fd100TargetHeight = logoSize - logoBottomMargin; // Equal margin top & bottom
    const fd100Scale = fd100TargetHeight / fd100LogoImg.height;
    const fd100Width = Math.floor(fd100LogoImg.width * fd100Scale);
    const fd100Height = Math.floor(fd100LogoImg.height * fd100Scale);
    const fd100LogoX = Math.floor(logoSpacing);
    const fd100LogoY = Math.floor(height - logoBottomMargin - fd100Height);
    ctx.drawImage(fd100LogoImg, fd100LogoX, fd100LogoY, fd100Width, fd100Height);

    // Partenaire logo (bottom right) - fixed bottom margin, independent of FD100 logo size
    if (partenaireLogoUrl) {
        try {
            const partenaireLogoImg = await loadImage(partenaireLogoUrl);

            // Use separate max width/height so horizontal logos can appear larger
            const partenaireMaxWidth = 250;
            const partenaireMaxHeight = 120;
            const baseScale = Math.min(partenaireMaxWidth / partenaireLogoImg.width, partenaireMaxHeight / partenaireLogoImg.height);

            // Apply per-partenaire override if exists
            const override = getPartenaireOverride(partenaireName);
            const scaleMultiplier = override?.scale || 1.0;
            const overrideOffsetY = override?.offsetY || 0;

            const partenaireScale = baseScale * scaleMultiplier;
            const partenaireWidth = Math.floor(partenaireLogoImg.width * partenaireScale);
            const partenaireHeight = Math.floor(partenaireLogoImg.height * partenaireScale);
            const partenaireLogoX = Math.floor(width - logoSpacing - partenaireWidth);
            // Position with fixed bottom margin + optional vertical offset
            const partenaireBottomY = height - logoBottomMargin;
            const partenaireLogoY = partenaireBottomY - partenaireHeight + overrideOffsetY;
            ctx.drawImage(partenaireLogoImg, partenaireLogoX, partenaireLogoY, partenaireWidth, partenaireHeight);
        } catch (err) {
            console.error('Error loading partenaire logo:', err);
            // Continue without the logo if it fails to load
        }
    }

    // Draw overlay lines (1080x1350)
    ctx.drawImage(overlayImg, 0, 0, width, height);

    // Convert canvas to blob
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Failed to generate image blob'));
            }
        }, 'image/png');
    });
}

/**
 * Generate and download FD100 KV image
 * @param {Object} projectData - Full project data including user info
 * @param {boolean} inverted - Whether to use inverted colors (blue bg)
 * @param {string} filename - Optional custom filename
 * @param {string} image_mode - Image fit mode: 'contain', 'cover', or 'debug'
 * @param {string} partenaireLogoUrl - Optional partenaire logo URL to display at bottom right
 */
export async function generateAndDownloadFD100KV(projectData, inverted = false, filename = null, image_mode = 'cover', partenaireLogoUrl = null, partenaireName = null, skipWordingMapping = false) {
    const blob = await generateFD100KV(projectData, inverted, image_mode, null, partenaireLogoUrl, partenaireName, skipWordingMapping);

    // Generate filename if not provided
    if (!filename) {
        const suffix = inverted ? '_b' : '_w';
        const projectName = (projectData.project.name_en || projectData.project.name || 'project').replace(/[^a-z0-9]/gi, '_');
        filename = `${projectName}_fd100_4_kv${suffix}.png`;
    }

    // Create download link
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Generate FD100 KV and return as data URL
 * @param {Object} projectData - Full project data including user info
 * @param {boolean} inverted - Whether to use inverted colors (blue bg)
 * @param {string} image_mode - Image fit mode: 'contain', 'cover', or 'debug'
 * @param {Object} editedData - Optional edited data from Supabase { projectName, creatorName }
 * @param {string} partenaireLogoUrl - Optional partenaire logo URL to display at bottom right
 * @returns {Promise<string>} - Returns a data URL of the generated image
 */
export async function generateFD100KVDataURL(projectData, inverted = false, image_mode = 'cover', editedData = null, partenaireLogoUrl = null, partenaireName = null, skipWordingMapping = false) {
    const blob = await generateFD100KV(projectData, inverted, image_mode, editedData, partenaireLogoUrl, partenaireName, skipWordingMapping);
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * Generate a generic FD100 KV for partenaires with no linked projects.
 * Uses the FD100 Instagram FR visual as background, overlays partner logo + text.
 * @param {string} partenaireName - The partenaire display name
 * @param {string} partenaireLogoUrl - The partenaire logo URL
 * @param {boolean} inverted - Kept for API compatibility (both variants use same background)
 * @returns {Promise<Blob>} - Returns a PNG blob of the generated image
 */
export async function generateGenericFD100KV(partenaireName, partenaireLogoUrl, inverted = false) {
    const width = 1080;
    const height = 1350;

    await loadFont('SuisseIntl-Bold', '/assets/SuisseIntl-SemiBold.ttf');
    await loadFont('SuisseIntl-LightItalic', '/assets/SuisseIntl-LightItalic.ttf');

    const bgImageUrl = '/assets/fd100-kit/visuals/instagram/FD100-POST-INSTA-FR.jpg';

    const loadPromises = [loadImage(bgImageUrl)];
    if (partenaireLogoUrl) { loadPromises.push(loadImage(partenaireLogoUrl).catch(() => null)); }
    const [bgImg, partenaireLogoImg] = await Promise.all(loadPromises);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Draw Instagram FR visual as full background (1080x1350)
    ctx.drawImage(bgImg, 0, 0, width, height);

    // Soft gradient fade at bottom to create a clean area for partner logo
    const fadeGradient = ctx.createLinearGradient(0, 750, 0, 920);
    fadeGradient.addColorStop(0, 'rgba(231, 231, 231, 0)');
    fadeGradient.addColorStop(1, 'rgba(231, 231, 231, 0.92)');
    ctx.fillStyle = fadeGradient;
    ctx.fillRect(0, 750, width, height - 750);

    // Partner logo right-aligned in bottom area
    if (partenaireLogoImg) {
        const partenaireMaxWidth = 900;
        const partenaireMaxHeight = 400;
        const baseScale = Math.min(partenaireMaxWidth / partenaireLogoImg.width, partenaireMaxHeight / partenaireLogoImg.height);

        const override = getPartenaireOverride(partenaireName);
        const scaleMultiplier = override?.scale || 1.0;
        const overrideOffsetY = override?.offsetY || 0;

        const partenaireScale = baseScale * scaleMultiplier;
        const partenaireWidth = Math.floor(partenaireLogoImg.width * partenaireScale);
        const partenaireHeight = Math.floor(partenaireLogoImg.height * partenaireScale);

        // Align right with 60px margin
        const logoX = width - 60 - partenaireWidth;
        const logoY = 880 + overrideOffsetY;

        ctx.drawImage(partenaireLogoImg, logoX, logoY, partenaireWidth, partenaireHeight);

        // "Partenaire du prix Le FD100" text below logo, right-aligned with logo
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const safariOffset = isSafari ? 13 : 0;
        const textY = logoY + partenaireHeight + 16;
        ctx.fillStyle = 'rgb(24, 51, 189)';
        ctx.textBaseline = 'top';
        ctx.letterSpacing = '0px';
        // Draw "Le FD100" bold first (right edge), then "Partenaire du prix " italic before it
        const rightEdge = width - 60;
        ctx.font = '42px SuisseIntl-Bold';
        const boldText = 'Le FD100';
        const boldWidth = ctx.measureText(boldText).width;
        ctx.textAlign = 'right';
        ctx.fillText(boldText, rightEdge, textY - safariOffset);
        ctx.font = '42px SuisseIntl-LightItalic';
        const italicText = 'Partenaire du prix ';
        ctx.fillText(italicText, rightEdge - boldWidth, textY - safariOffset);
    }

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to generate image blob'));
        }, 'image/png');
    });
}

export async function generateGenericFD100KVDataURL(partenaireName, partenaireLogoUrl, inverted = false) {
    const blob = await generateGenericFD100KV(partenaireName, partenaireLogoUrl, inverted);
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * Generate a "classic" generic FD100 KV (gray/blue background like regular project KVs).
 * FD100 "AWARDS 4e édition" logo centered, partner logo centered below, subtitle text.
 * @param {string} partenaireName - The partenaire display name
 * @param {string} partenaireLogoUrl - The partenaire logo URL
 * @param {boolean} inverted - false = gray bg + blue logo, true = blue bg + white logo
 * @returns {Promise<Blob>} - Returns a PNG blob of the generated image
 */
export async function generateGenericFD100KVClassic(partenaireName, partenaireLogoUrl, inverted = false) {
    // Same layout constants as regular KVs
    const width = 1080;
    const height = 1350;
    const bgColor = inverted ? 'rgb(24, 51, 189)' : 'rgb(231, 231, 231)';
    const textColor = inverted ? 'rgb(231, 231, 231)' : 'rgb(24, 51, 189)';
    const topMargin = 42;
    const textLeftPadding = 30;
    const logoBottomMargin = 28;
    const logoSize = 260;
    const logoSpacing = 26;
    const nameSize = 60;

    await loadFont('SuisseIntl-Bold', '/assets/SuisseIntl-SemiBold.ttf');
    await loadFont('SuisseIntl-LightItalic', '/assets/SuisseIntl-LightItalic.ttf');

    // FD100 "AWARDS 4e édition" logo (white for blue bg, blue for gray bg)
    const fd100LogoUrl = inverted ? '/assets/FD100_4_logo_b.png' : '/assets/FD100_4_logo_w.png';
    const overlayUrl = '/assets/overlay_lines.png';

    const loadPromises = [loadImage(fd100LogoUrl), loadImage(overlayUrl)];
    if (partenaireLogoUrl) { loadPromises.push(loadImage(partenaireLogoUrl).catch(() => null)); }
    const [fd100LogoImg, overlayImg, partenaireLogoImg] = await Promise.all(loadPromises);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Fill background
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, width, height);

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const safariOffset = isSafari ? 13 : 0;

    // Partner name top-left (same position as project name in regular KVs)
    ctx.fillStyle = textColor;
    ctx.font = `${nameSize}px SuisseIntl-Bold`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.letterSpacing = '-1px';
    ctx.fillText(partenaireName || '', textLeftPadding, topMargin - safariOffset);

    // Calculate image area (same as regular KVs)
    const textSectionHeight = topMargin + nameSize * 1.1 + 32;
    const bottomSectionHeight = logoBottomMargin + logoSize;
    const imgHorizontalPadding = 30;
    const availableImageWidth = width - 2 * imgHorizontalPadding;
    const availableImageHeight = height - textSectionHeight - bottomSectionHeight - 15;
    const imageTop = Math.floor(textSectionHeight);
    const imageLeft = Math.floor((width - availableImageWidth) / 2);

    // Partner logo centered in the image area (1.5x bigger)
    if (partenaireLogoImg) {
        const partenaireMaxWidth = 900;
        const partenaireMaxHeight = 525;
        const baseScale = Math.min(partenaireMaxWidth / partenaireLogoImg.width, partenaireMaxHeight / partenaireLogoImg.height);

        const override = getPartenaireOverride(partenaireName);
        const scaleMultiplier = override?.scale || 1.0;
        const overrideOffsetY = override?.offsetY || 0;

        const partenaireScale = baseScale * scaleMultiplier;
        const partenaireWidth = Math.floor(partenaireLogoImg.width * partenaireScale);
        const partenaireHeight = Math.floor(partenaireLogoImg.height * partenaireScale);

        const logoX = imageLeft + (availableImageWidth - partenaireWidth) / 2;
        const logoY = imageTop + (availableImageHeight - partenaireHeight) / 2 + overrideOffsetY;

        ctx.drawImage(partenaireLogoImg, logoX, logoY, partenaireWidth, partenaireHeight);
    }

    // "Partenaire du prix Le FD100" text bottom-right
    const rightEdge = width - 60;
    const textBottomY = height - logoBottomMargin - 20;
    ctx.fillStyle = textColor;
    ctx.textBaseline = 'bottom';
    ctx.letterSpacing = '0px';
    ctx.font = '42px SuisseIntl-Bold';
    const boldText = 'Le FD100';
    const boldWidth = ctx.measureText(boldText).width;
    ctx.textAlign = 'right';
    ctx.fillText(boldText, rightEdge, textBottomY - safariOffset);
    ctx.font = '42px SuisseIntl-LightItalic';
    const italicText = 'Partenaire du prix ';
    ctx.fillText(italicText, rightEdge - boldWidth, textBottomY - safariOffset);

    // Overlay lines
    ctx.drawImage(overlayImg, 0, 0, width, height);

    // FD100 "AWARDS 4e édition" logo - bottom left
    const fd100TargetHeight = logoSize - logoBottomMargin;
    const fd100Scale = fd100TargetHeight / fd100LogoImg.height;
    const fd100Width = Math.floor(fd100LogoImg.width * fd100Scale);
    const fd100Height = Math.floor(fd100LogoImg.height * fd100Scale);
    ctx.drawImage(fd100LogoImg, logoSpacing, height - logoBottomMargin - fd100Height, fd100Width, fd100Height);

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to generate image blob'));
        }, 'image/png');
    });
}

export async function generateGenericFD100KVClassicDataURL(partenaireName, partenaireLogoUrl, inverted = false) {
    const blob = await generateGenericFD100KVClassic(partenaireName, partenaireLogoUrl, inverted);
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}
